import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const Container = styled.div<{ compact?: boolean; section?: boolean }>`
  // RES-999: compact usage (booking-details card) supplies its own divider above this block —
  // skip the default top border rather than stacking two dividers back to back.
  // RES-1316: a section block owns its own card, so that card border IS the divider and
  // this one only stacked a stray rule above the heading. No backticks here: inside the template.
  ${({ theme, compact, section }) =>
    compact || section ? '' : `border-top: 1px solid ${theme.colors.greys.adminGreyLight};`}
`;

// RES-1316: `section` keeps the bold heading but drops the 28px lead-in, which inside a card of its
// own read as a large empty band above the title.
/* A section block opens its own card, so it needs no lead-in above the heading at all. */
const leadIn = ({ compact, section }: { compact?: boolean; section?: boolean }): string => {
  if (section) {
    return '0';
  }

  return compact ? '16px' : '28px';
};

export const Header = styled.div<{ compact?: boolean; section?: boolean }>`
  margin-top: ${leadIn};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }

  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const Icon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.$primary};
`;

// RES-1316: the default caret renders at 2x, which towers over a 16px heading and forces the row
// taller than the text needs.
export const SectionIcon = styled(FontAwesomeIcon)`
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.$primary};
`;

// RES-999: the default Icon renders at size="2x" in the app's primary (usually blue/brand) color
// — oversized and off-palette against the booking-details card's small grey labels/icons
// (SectionIcon et al). Compact mode uses this smaller, muted-grey caret instead, sized to match.
/* The same muted caret the pane module uses, so two cards side by side fold the same way. */
export const CardIcon = styled(FontAwesomeIcon)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const CompactIcon = styled(FontAwesomeIcon)`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const Title = styled.div`
  font: 700 16px/19px ${({ theme }) => theme.fonts.join()};
`;

/*
 * RES-1367: the heading of a block that owns its own card, matching the card titles around it --
 * the booking's resource name and the conversation's "Messages" -- rather than the smaller
 * sub-heading a section inside a card gets.
 */
export const CardTitle = styled.div`
  font: 600 20px/1.3 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

// RES-999: matches the booking-details card's other small grey labels (When/Location/Reserved
// by) instead of this block's normal bold heading.
export const CompactTitle = styled.div`
  font: 600 13px/1 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const Content = styled.div<{ compact?: boolean; section?: boolean }>`
  height: auto;
  margin-top: ${({ compact, section }) => (compact || section ? '16px' : '28px')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.colors.fontColor};
`;
